// Cloud Functions API handlers — App Assistant (Aria) domain.

import { ApiResponse } from '../../types'
import { CloudFunction } from '../../../../../pure-js/enums/CloudFunction'
import type {
  MarkAssistantSupportRepliesReadRequest,
  ResolveAssistantSupportRequestRequest,
  ResolveAssistantSupportRequestResponse,
  SendAssistantSupportMessageRequest,
  SendAssistantSupportMessageResponse,
  MarkAssistantSupportRepliesReadResponse
} from '../../../../../pure-js/types/AssistantEscalation'
import {
  AppAssistantActionType,
  type AppAssistantRequest,
  type AppAssistantResult,
  type AssistantLiveTokenResult,
  type AssistantUserStatus,
  type AssistantUserStatusRequest,
  type EscalateAssistantIssueRequest,
  type EscalateAssistantIssueResult,
  type MintAssistantLiveTokenRequest
} from '../../../../../pure-js/types/AppAssistant'
import {
  AssistantMessageKey,
  AssistantUnsupportedReason,
  translatedMessage
} from '../../../../../pure-js/libs/assistantMessages'
import { callEnvelopeCloudFunction } from './callCloudFunction'

/**
 * The call itself failed (offline, transport error). The reply is composed here — still as a
 * translation key, like every other deterministic reply.
 */
const REQUEST_FAILED_RESULT: AppAssistantResult = {
  message: translatedMessage(AssistantMessageKey.REQUEST_FAILED),
  action: { type: AppAssistantActionType.UNSUPPORTED, reason: AssistantUnsupportedReason.NOT_UNDERSTOOD }
}

export const askAppAssistant = async (req: AppAssistantRequest): Promise<ApiResponse<AppAssistantResult>> =>
  callEnvelopeCloudFunction(CloudFunction.askAppAssistant, req, 'Failed to ask app assistant', REQUEST_FAILED_RESULT)

export const getAssistantUserStatus = async (
  req: AssistantUserStatusRequest
): Promise<ApiResponse<AssistantUserStatus | undefined>> =>
  callEnvelopeCloudFunction<AssistantUserStatusRequest, AssistantUserStatus | undefined>(
    CloudFunction.getAssistantUserStatus,
    req,
    'Failed to get assistant status',
    undefined
  )

export const escalateAssistantIssue = async (
  req: EscalateAssistantIssueRequest
): Promise<ApiResponse<EscalateAssistantIssueResult>> =>
  callEnvelopeCloudFunction(CloudFunction.escalateAssistantIssue, req, 'Failed to escalate issue', {
    delivered: false
  })

export const mintAssistantLiveToken = async (
  req: MintAssistantLiveTokenRequest
): Promise<ApiResponse<AssistantLiveTokenResult>> =>
  callEnvelopeCloudFunction(CloudFunction.mintAssistantLiveToken, req, 'Failed to start live assistant', {})

export const sendAssistantSupportMessage = async (
  req: SendAssistantSupportMessageRequest
): Promise<ApiResponse<SendAssistantSupportMessageResponse['data']>> =>
  callEnvelopeCloudFunction<SendAssistantSupportMessageRequest, SendAssistantSupportMessageResponse['data']>(
    CloudFunction.sendAssistantSupportMessage,
    req,
    'Failed to send your message to support',
    { sent: false }
  )

export const resolveAssistantSupportRequest = async (
  req: ResolveAssistantSupportRequestRequest
): Promise<ApiResponse<ResolveAssistantSupportRequestResponse['data']>> =>
  callEnvelopeCloudFunction<ResolveAssistantSupportRequestRequest, ResolveAssistantSupportRequestResponse['data']>(
    CloudFunction.resolveAssistantSupportRequest,
    req,
    'Failed to close your support request',
    { resolved: false }
  )

export const markAssistantSupportRepliesRead = async (
  req: MarkAssistantSupportRepliesReadRequest
): Promise<ApiResponse<MarkAssistantSupportRepliesReadResponse['data']>> =>
  callEnvelopeCloudFunction<MarkAssistantSupportRepliesReadRequest, MarkAssistantSupportRepliesReadResponse['data']>(
    CloudFunction.markAssistantSupportRepliesRead,
    req,
    'Failed to mark support replies read',
    { marked: 0 }
  )
